import { Component, h, Host, Prop, State, Method, Element, Build, Watch } from '@stencil/core';
import { getConstant } from '../../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-popup-form-loader';

const PHRASE_INTERVAL_MS = 3300;
const REDIRECT_DELAY_MS = 400;
const ICON_URL = 'https://marketing-static.signnow.com/366/images/global/modals/icon-loader.svg';

/**
 * Full-screen form loader overlay that shows a progress bar animation and rotating
 * status phrases while fetching editor data from the server. Opens on
 * `sn-form-loader-open` custom event or programmatically via `open()` method.
 * Closes on successful redirect or on error. Pressing Escape during loading
 * triggers a page reload. Blocks body scrolling while visible.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 *
 * @example
 *   <sn-popup-form-loader
 *     editor-data-url="/forms/get-editor-data"
 *   ></sn-popup-form-loader>
 */
@Component({
  tag: 'sn-popup-form-loader',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnPopupFormLoader {
  @Element() host: HTMLElement;

  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * URL endpoint for fetching editor data
   * @default "/forms/get-editor-data"
   */
  @Prop({ attribute: 'editor-data-url' }) editorDataUrl: string = '/forms/get-editor-data';

  /**
   * Controls loader visibility
   * @default false
   */
  @Prop({ mutable: true, reflect: true }) isOpen: boolean = false;

  /**
   * Component constants object containing all required text constants
   * @constant FORM_LOADER_PHRASE_1 - "Establishing secure connection…"
   * @constant FORM_LOADER_PHRASE_2 - "Loading editor…"
   * @constant FORM_LOADER_PHRASE_3 - "Preparing document…"
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  @State() activePhraseIndex: number = 0;
  @State() isBrowser: boolean = false;

  private ctaClickFlag = true;
  private phraseTimerId?: ReturnType<typeof setInterval>;
  private abortController?: AbortController;

  @Watch('isOpen')
  watchIsOpen(newValue: boolean) {
    if (!Build.isBrowser) return;
    if (newValue) {
      this.lockBody();
      this.startPhraseRotation();
    } else {
      this.unlockBody();
      this.stopPhraseRotation();
    }
  }

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  connectedCallback() {
    if (!Build.isBrowser) return;
    this.isBrowser = true;
    window.addEventListener('sn-form-loader-open', this.handleOpenEvent);
    window.addEventListener('pageshow', this.handlePageShow);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    window.removeEventListener('sn-form-loader-open', this.handleOpenEvent);
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.stopPhraseRotation();
    this.unlockBody();
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Safari on iOS restores a page from the back/forward cache instead of reloading it, so
   * navigating back from the editor replays the DOM exactly as it was left — overlay still up,
   * body still scroll-locked, and `ctaClickFlag` still spent, which also swallowed the next
   * "Get Form" click. Desktop browsers only masked the bug by reloading. `pageshow` with
   * `persisted` is the one signal a bfcache restore emits, so the loader resets there.
   */
  private handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      this.reset();
    }
  };

  /** Back to idle: overlay hidden, body unlocked, click guard re-armed, request dropped. */
  private reset() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
    // `isOpen` is watched — the watcher unlocks the body and stops the phrase rotation.
    this.isOpen = false;
    this.ctaClickFlag = true;
    this.activePhraseIndex = 0;
  }

  private handleOpenEvent = (event: CustomEvent<string>) => {
    const formId = event.detail;
    if (formId) {
      this.loadForm(formId);
    }
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (!this.isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setTimeout(() => {
        window.location.reload();
      }, 200);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  private lockBody() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add(`${COMPONENT_NAME}-open`);
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  private unlockBody() {
    document.body.classList.remove(`${COMPONENT_NAME}-open`);
    document.body.style.paddingRight = '';
  }

  private startPhraseRotation() {
    this.activePhraseIndex = 0;
    this.phraseTimerId = setInterval(() => {
      this.activePhraseIndex = Math.min(this.activePhraseIndex + 1, 2);
    }, PHRASE_INTERVAL_MS);
  }

  private stopPhraseRotation() {
    if (this.phraseTimerId) {
      clearInterval(this.phraseTimerId);
      this.phraseTimerId = undefined;
    }
  }

  private resolveEditorDataUrl(): string {
    if (this.editorDataUrl.startsWith('http')) {
      return this.editorDataUrl;
    }
    const { protocol, hostname } = window.location;
    const parts = hostname.split('.');
    const normalizedHost = parts.length > 2 ? `www.${parts.slice(-2).join('.')}` : hostname;
    return `${protocol}//${normalizedHost}${this.editorDataUrl}`;
  }

  /**
   * Initiates the form loading flow for the given formId
   */
  @Method()
  async loadForm(formId: string) {
    if (!Build.isBrowser) return;
    if (!this.ctaClickFlag) return;

    this.isOpen = true;
    this.ctaClickFlag = false;

    const csrfMeta = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']");
    const token = csrfMeta?.content || '';

    this.abortController = new AbortController();

    try {
      const response = await fetch(this.resolveEditorDataUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-TOKEN': token,
        },
        body: `id=${encodeURIComponent(formId)}`,
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const { editorUrl } = data;

      if (editorUrl) {
        setTimeout(() => {
          window.location.href = editorUrl;
        }, REDIRECT_DELAY_MS);
      } else {
        this.handleError();
      }
    } catch {
      this.handleError();
    }
  }

  private handleError() {
    setTimeout(() => this.reset(), 4000);
  }

  /**
   * Opens the loader overlay
   */
  @Method()
  async open(formId: string) {
    this.loadForm(formId);
  }

  /**
   * Closes the loader overlay
   */
  @Method()
  async close() {
    this.isOpen = false;
    this.ctaClickFlag = true;
  }

  render() {
    if (!this.isBrowser) return null;

    const phrases = [
      getConstant(this.constants, 'FORM_LOADER_PHRASE_1', 'Establishing secure connection…'),
      getConstant(this.constants, 'FORM_LOADER_PHRASE_2', 'Loading editor…'),
      getConstant(this.constants, 'FORM_LOADER_PHRASE_3', 'Preparing document…'),
    ];

    return (
      <Host data-color-mode={this.colorMode}>
        <div
          class={{
            [COMPONENT_NAME]: true,
            [`${COMPONENT_NAME}--visible`]: this.isOpen,
          }}
          id="form-loader"
          role="dialog"
          aria-modal="true"
          aria-label="Loading form editor"
        >
          <div class={`${COMPONENT_NAME}__body`}>
            <div class={`${COMPONENT_NAME}__icon`}>
              <img src={ICON_URL} alt="" aria-hidden="true" width="176" height="95" />
            </div>

            <div class={`${COMPONENT_NAME}__progress-bar`}>
              <div
                class={{
                  [`${COMPONENT_NAME}__progress-fill`]: true,
                  [`${COMPONENT_NAME}__progress-fill--active`]: this.isOpen,
                }}
              ></div>
            </div>

            <div class={`${COMPONENT_NAME}__phrases`}>
              {phrases.map((phrase, idx) => (
                <span
                  class={{
                    [`${COMPONENT_NAME}__phrase`]: true,
                    [`${COMPONENT_NAME}__phrase--active`]: idx === this.activePhraseIndex,
                  }}
                  key={phrase}
                >
                  {phrase}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
